import {
    ArrowRightIcon,
    BanknotesIcon,
    CreditCardIcon,
    DocumentTextIcon,
    PhoneIcon,
    SparklesIcon,
    UserGroupIcon,
} from "@heroicons/react/24/outline";
import { formatRate } from "../../utils/formatRate";
import { pricingTheme, type PricingTone } from "./pricingTheme";
import type { PublicRateCard } from "./rateCardTypes";

interface Props {
    tone: PricingTone;
    card: PublicRateCard;
}

/**
 * The three things a workspace pays for, and how each is collected. A plain
 * version of the billing flow: use → credits first → balance → collected.
 */
export default function HowBillingWorks({ tone, card }: Props) {
    const t = pricingTheme(tone);
    const isInr = card.currency === "inr";
    const threshold = card.billing.voiceAutoChargeAtMinor;

    const parts = [
        {
            icon: CreditCardIcon,
            title: "Your plan",
            body: `A monthly or yearly subscription for the platform — chat widgets, knowledge base, workflows and your team. Billed in advance${
                isInr ? " through Razorpay" : " through Stripe"
            }.`,
        },
        {
            icon: PhoneIcon,
            title: "Usage",
            body: "AI voice minutes, warm transfers, SMS and phone numbers, metered as you use them and priced below. Nothing is charged until you use it.",
        },
        {
            icon: UserGroupIcon,
            title: "Call centre",
            body: "Your own agents on the call-centre floor. Agent minutes and floor numbers run on a balance of their own, billed separately from AI voice.",
        },
    ];

    const flow = [
        { icon: PhoneIcon, label: "You use it" },
        { icon: SparklesIcon, label: "Credits are used first" },
        { icon: BanknotesIcon, label: "The rest adds to a balance" },
        {
            icon: isInr ? DocumentTextIcon : CreditCardIcon,
            label: isInr
                ? "Collected with your Razorpay renewal or a monthly invoice"
                : `Charged to your card at ${threshold ? formatRate(threshold, card.currency) : "a threshold"}`,
        },
    ];

    return (
        <section aria-labelledby="how-billing-works" className={t.section}>
            <h3 id="how-billing-works" className={`text-xl font-bold ${t.heading}`}>
                How you&apos;re charged
            </h3>
            <p className={`mt-1 text-sm ${t.body}`}>
                Prices in {card.currency.toUpperCase()}. Every amount on this page comes from the live rate card.
            </p>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
                {parts.map(({ icon: Icon, title, body }) => (
                    <div key={title} className={t.card}>
                        <Icon className={`h-6 w-6 ${t.accent}`} aria-hidden="true" />
                        <h4 className={`mt-3 text-base font-semibold ${t.subheading}`}>{title}</h4>
                        <p className={`mt-1 text-sm ${t.body}`}>{body}</p>
                    </div>
                ))}
            </div>

            <ol className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center" aria-label="How usage is billed">
                {flow.map(({ icon: Icon, label }, i) => (
                    <li key={label} className="flex items-center gap-3 lg:flex-1">
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${t.accentBg}`}>
                            <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className={`text-sm ${t.body}`}>{label}</span>
                        {i < flow.length - 1 && <ArrowRightIcon className={`hidden h-4 w-4 flex-shrink-0 lg:block ${t.muted}`} aria-hidden="true" />}
                    </li>
                ))}
            </ol>

            {!isInr && card.billing.minimumChargeMinor > 0 && (
                <p className={`mt-4 text-xs ${t.muted}`}>
                    AI voice, call centre and SMS are each collected separately. Anything left below the threshold is collected at month end; a balance under{" "}
                    {formatRate(card.billing.minimumChargeMinor, card.currency)} rolls into the next month.
                </p>
            )}
        </section>
    );
}
